import type { Device } from '@sites/entity-core';

/**
 * Writes one breakpoint of a responsive FieldTree node.
 *
 * `tablet` and `mobile` are optional on the responsive schema. Every entity the
 * migration produced carries all three, but a hand-built one need not — in that
 * case the write goes through the responsive node itself, which both stores the
 * value and materialises the child for every write after it.
 *
 * Deliberately untyped: `FieldTree` is invariant in its model, so a helper shared
 * by every responsive leaf cannot name a parameter type all of them satisfy. The
 * accessors that call it are typed, and so is everything they hand back.
 */
export const writeDevice = (responsive: any, device: Device, update: (current: any) => any): void => {
    const leaf = responsive[device];

    if (leaf) {
        leaf().value.update(update);
        return;
    }

    responsive().value.update((current: any) => ({
        ...current,
        [device]: update(current?.[device] ?? current?.desktop),
    }));
};

/**
 * The child node under `key`, seeded first when the model has none — a group that
 * was compacted to its switch keeps no values, so a write into it has to put
 * them back before it can address them.
 */
export const ensureChild = (parent: any, key: string, seed: () => unknown): any => {
    if (parent()?.value()?.[key] === undefined) {
        parent().value.update((current: any) => ({ ...current, [key]: seed() }));
    }

    return parent[key];
};
